import { PERMISSION_KEYS } from './permissionKeys'

/**
 * @typedef {Object} NavigationLink
 * @property {string} id
 * @property {string} label
 * @property {string} [routeName] - customNavigation route name
 * @property {'contact'|'privacy'} [action]
 * @property {boolean} [saveEvent]
 * @property {string[]} [permissions] - user must have at least one
 * @property {string[]} [hiddenWhenPermissions] - hide when user has any of these
 */

/** Primary navbar items — filtered by permissions, no branching lists. */
export function buildNavbarLinks(routes) {
  return /** @type {NavigationLink[]} */ ([
    {
      id: 'home',
      label: 'Home',
      routeName: routes.Home.name,
      // Transform-only users have a Home too: it renders the library-style layout with the
      // Content Library cards switched off, instead of redirecting them to the dashboard.
      permissions: [
        PERMISSION_KEYS.CORE_ACCESS,
        PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS,
        PERMISSION_KEYS.TRANSFORM_ACCESS
      ]
    },
    {
      id: 'employers',
      label: 'Employers',
      routeName: routes.ListOfEmployers.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'actionCenter',
      label: 'Book Overview & Action Center',
      routeName: routes.ActionCenter.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'helpAndResources',
      label: 'Help & Resources',
      routeName: routes.helpAndResources.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'contentLibrary',
      label: 'Content Library',
      routeName: routes.contentLibrary.name,
      permissions: [PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS],
      hiddenWhenPermissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'transform',
      label: 'BCS Transform',
      routeName: routes.transformLearningDashboard.name,
      permissions: [PERMISSION_KEYS.TRANSFORM_ACCESS],
      hiddenWhenPermissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'faqs',
      label: 'FAQs',
      routeName: routes.frequentlyQuestions.name,
      permissions: [PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS, PERMISSION_KEYS.TRANSFORM_ACCESS],
      hiddenWhenPermissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'adminCenter',
      label: 'Admin Center',
      routeName: routes.AdminCenter.name,
      permissions: [PERMISSION_KEYS.ADMIN_CENTER_ACCESS]
    }
  ])
}

/**
 * Footer links in display order (feature branch parity, permission-gated).
 * Visible links fill columns sequentially (max per column), adding columns as needed.
 */
export const FOOTER_MAX_LINKS_PER_COLUMN = 4

export function buildFooterLinks(routes) {
  return /** @type {NavigationLink[]} */ ([
    {
      id: 'home',
      label: 'Home',
      routeName: routes.Home.name,
      // Transform-only users have a Home too: it renders the library-style layout with the
      // Content Library cards switched off, instead of redirecting them to the dashboard.
      permissions: [
        PERMISSION_KEYS.CORE_ACCESS,
        PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS,
        PERMISSION_KEYS.TRANSFORM_ACCESS
      ]
    },
    {
      id: 'help',
      label: 'Help',
      routeName: routes.helpAndResources.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    { id: 'contact', label: 'Contact', action: 'contact' },
    { id: 'profile', label: 'Profile', routeName: routes.memberProfile.name },
    {
      id: 'employers',
      label: 'All Employer Dashboard',
      routeName: routes.ListOfEmployers.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'trainingResources',
      label: 'Training and Resources',
      routeName: routes.helpAndResources.name,
      permissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'contentLibrary',
      label: 'Content Library',
      routeName: routes.contentLibrary.name,
      permissions: [PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS],
      hiddenWhenPermissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'faqs',
      label: 'Frequently Asked Questions',
      routeName: routes.frequentlyQuestions.name,
      permissions: [
        PERMISSION_KEYS.CORE_ACCESS,
        PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS,
        PERMISSION_KEYS.TRANSFORM_ACCESS
      ]
    },
    {
      id: 'privacy',
      label: 'Privacy Policy',
      action: 'privacy',
      permissions: [PERMISSION_KEYS.CONTENT_LIBRARY_ACCESS],
      hiddenWhenPermissions: [PERMISSION_KEYS.CORE_ACCESS]
    },
    {
      id: 'terms',
      label: 'Terms & Conditions',
      routeName: routes.termsAndConditions.name,
      saveEvent: true
    }
  ])
}

/** Fill footer columns sequentially — up to maxPerColumn links each, then next column. */
export function distributeFooterLinksAcrossColumns(links, authStore, maxPerColumn = FOOTER_MAX_LINKS_PER_COLUMN) {
  const visible = links.filter((link) => isNavigationLinkVisible(link, authStore))
  if (visible.length === 0) return [[]]

  const columns = []
  for (let i = 0; i < visible.length; i += maxPerColumn) {
    columns.push(visible.slice(i, i + maxPerColumn))
  }
  return columns
}

export function isNavigationLinkVisible(link, authStore) {
  if (link.permissions?.length && !authStore.hasAnyPermission(link.permissions)) {
    return false
  }
  if (link.hiddenWhenPermissions?.length && authStore.hasAnyPermission(link.hiddenWhenPermissions)) {
    return false
  }
  return true
}
